import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../lib/icons";
import { POST_STATUS, POST_PLATFORMS, POST_FORMATS } from "../lib/helpers";

// Calendrier éditorial (décisions de Mélilia du 29/09, sur ébauche) : quatre vues inspirées de Google Agenda —
// jour, semaine (vue d'ouverture), mois, année. Publications seulement, pas de rendez-vous. Une publication peut
// porter une heure « HH:MM » (sans fuseau) : la semaine et le jour sont une grille horaire, et les publications
// sans heure se rangent dans la bande « Sans heure ». L'agence glisse une publication d'un jour à l'autre, et
// d'une heure à l'autre dans la grille (comme Google : dans le mois l'heure est gardée, dans la bande elle est
// retirée). Sur téléphone : listes au lieu des grilles, et pas de glisser-déposer.

const VIEWS = [["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"], ["annee", "Année"]];
const DAY_SHORT = ["LUN.", "MAR.", "MER.", "JEU.", "VEN.", "SAM.", "DIM."];
const DAY_INITIAL = ["L", "M", "M", "J", "V", "S", "D"];
const HOUR = 48; // hauteur d'une heure dans la grille, en pixels
const SNAP = 15; // pas du glisser-déposer, en minutes
const NARROW_QUERY = "(max-width: 767px)";

// Couleur d'une publication selon l'intention de son statut (lib/helpers.js, INTENT). Classes écrites en entier
// pour que Tailwind les génère.
const TONE = {
  inerte: { chip: "bg-sable-clair border-marine/20 hover:border-marine/50", dot: "bg-muet" },
  en_cours: { chip: "bg-marine-50 border-marine/30 hover:border-marine", dot: "bg-marine" },
  attente: { chip: "bg-ambre-50 border-ambre/30 hover:border-ambre", dot: "bg-ambre" },
  echec: { chip: "bg-rouge-50 border-rouge/30 hover:border-rouge", dot: "bg-rouge" },
  acquis: { chip: "bg-bleu-50 border-bleu/30 hover:border-bleu", dot: "bg-bleu" },
};
const RANK = { echec: 0, attente: 1, en_cours: 2, acquis: 3, inerte: 4 }; // point de l'année : le plus pressant
const LEGEND = [["echec", "À corriger"], ["attente", "À valider"], ["en_cours", "Programmé"], ["acquis", "Validé ou publié"]];

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const fmt = (d, o) => new Intl.DateTimeFormat("fr-FR", o).format(d);
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const toMin = (t) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const toHHMM = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
const nowMinutes = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };
export const fmtDayLong = (d) => fmt(typeof d === "string" ? parseISO(d) : d, { weekday: "long", day: "numeric", month: "long" });

const statusOf = (p) => POST_STATUS[p.status] || POST_STATUS.a_valider;
const toneOf = (p) => TONE[statusOf(p).intent] || TONE.attente;
const dayKey = (p) => (p.date || "").slice(0, 10);
const titleOf = (p) => p.theme || `${(POST_FORMATS[p.format] || {}).label || "Publication"} ${(p.platforms || [])[0] || ""}`.trim();
const byTime = (a, b) => (a.time ? toMin(a.time) : -1) - (b.time ? toMin(b.time) : -1) || titleOf(a).localeCompare(titleOf(b));

function PlatformIcon({ post }) {
  const meta = POST_PLATFORMS[(post.platforms || [])[0]];
  const Ic = (meta && ICONS_BY_NAME[meta.icon]) || Icons.Share2;
  return <Ic size={13} className="shrink-0" aria-hidden="true" />;
}

// Un élément cliquable ET déplaçable : une <div role="button"> plutôt qu'un <button>, que Firefox refuse de glisser.
function Clickable({ onActivate, className, style, label, testid, drag, children }) {
  return (
    <div role="button" tabIndex={0} aria-label={label} title={label} data-testid={testid} style={style}
      className={`select-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marine ${className}`}
      onClick={onActivate}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate(); } }}
      {...drag}>
      {children}
    </div>
  );
}

// Colonnes des publications qui se chevauchent (une heure chacune), groupe par groupe comme Google :
// une publication seule garde toute la largeur.
function layoutLanes(list) {
  const out = []; let group = null;
  list.forEach((p) => {
    const s = toMin(p.time);
    if (!group || s >= group.end) { group = { end: 0, lanes: [], items: [] }; out.push(group); }
    let lane = group.lanes.findIndex((e) => e <= s);
    if (lane < 0) { lane = group.lanes.length; group.lanes.push(0); }
    group.lanes[lane] = s + 60; group.end = Math.max(group.end, s + 60); group.items.push({ p, lane });
  });
  return out.flatMap((g) => g.items.map((it) => ({ ...it, lanes: g.lanes.length })));
}

function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const m = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

function useNow() {
  const [now, setNow] = useState(nowMinutes);
  useEffect(() => { const t = setInterval(() => setNow(nowMinutes()), 60000); return () => clearInterval(t); }, []);
  return now;
}

export default function EditorialCalendar({ posts = [], clientNames = null, onPostClick, onCreate, onMove, toolbarExtra }) {
  const [view, setView] = useState("semaine");
  const [cursor, setCursor] = useState(() => new Date());
  const [picked, setPicked] = useState(null); // jour touché dans le mois, sur téléphone
  const [over, setOver] = useState(null); // cible survolée pendant un glisser : { key, band?, min? }
  const [draggingId, setDraggingId] = useState(null);
  const dragRef = useRef(null); // { id, grabPx } : lu pendant le survol sans provoquer de rendu
  const scrollRef = useRef(null);
  const narrow = useNarrow();
  const now = useNow();
  const canDrag = Boolean(onMove) && !narrow;
  const today = iso(new Date());

  const byDay = useMemo(() => {
    const o = {};
    posts.forEach((p) => { const k = dayKey(p); if (k) (o[k] = o[k] || []).push(p); });
    Object.values(o).forEach((l) => l.sort(byTime));
    return o;
  }, [posts]);

  const weekDays = useMemo(() => { const s = startOfWeek(cursor); return Array.from({ length: 7 }, (_, i) => addDays(s, i)); }, [cursor]);
  const shownDays = view === "semaine" ? weekDays : view === "jour" ? [cursor] : [];
  const hasPosts = posts.length > 0;

  // Grille horaire : on arrive une heure avant la première publication visible (9:00 par défaut, midi au plus tard).
  // Seulement quand la vue, la période ou l'écran changent — pas après un glisser-déposer.
  const scrollKey = `${view}|${iso(cursor)}|${narrow}|${hasPosts}`;
  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const keys = (view === "semaine" ? weekDays : [cursor]).map(iso);
    const first = posts.filter((p) => p.time && keys.includes(dayKey(p))).reduce((a, p) => Math.min(a, toMin(p.time)), 9 * 60);
    el.scrollTop = Math.max(0, ((Math.min(first, 12 * 60) - 60) / 60) * HOUR - 14);
  }, [scrollKey]); // eslint-disable-line react-hooks/exhaustive-deps -- seul scrollKey doit déclencher le défilement (pas chaque déplacement)

  const shift = (n) => setCursor((d) => (view === "mois" ? new Date(d.getFullYear(), d.getMonth() + n, 1)
    : view === "annee" ? new Date(d.getFullYear() + n, d.getMonth(), 1)
      : addDays(d, view === "semaine" ? 7 * n : n)));
  const openDay = (key) => { setCursor(parseISO(key)); setView("jour"); };
  const openMonth = (y, m) => { setCursor(new Date(y, m, 1)); setView("mois"); setPicked(null); };

  const period = (() => {
    if (view === "mois") return cap(fmt(cursor, { month: "long", year: "numeric" }));
    if (view === "annee") return String(cursor.getFullYear());
    if (view === "jour") return cap(fmt(cursor, { weekday: "long", day: "numeric", month: "long", year: "numeric" }));
    const s = weekDays[0], e = weekDays[6];
    return s.getMonth() === e.getMonth()
      ? `${s.getDate()} – ${fmt(e, { day: "numeric", month: "long", year: "numeric" })}`
      : `${fmt(s, { day: "numeric", month: "short" })} – ${fmt(e, { day: "numeric", month: "short", year: "numeric" })}`;
  })();

  // ---- Glisser-déposer (agence, écran large) ----
  const endDrag = () => { dragRef.current = null; setDraggingId(null); setOver(null); };
  const dragProps = (p) => (canDrag ? {
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", p.id);
      dragRef.current = { id: p.id, grabPx: e.clientY - e.currentTarget.getBoundingClientRect().top };
      setTimeout(() => setDraggingId(p.id), 0); // après la capture de l'image glissée
    },
    onDragEnd: endDrag,
  } : {});
  const minutesAt = (e, step, grabPx, round) => {
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top - grabPx;
    const m = (round ? Math.round : Math.floor)(((y / HOUR) * 60) / step) * step;
    return Math.min(Math.max(m, 0), 24 * 60 - step);
  };
  const place = (key, time) => {
    const p = dragRef.current && posts.find((x) => x.id === dragRef.current.id);
    endDrag();
    if (p) onMove(p, { date: key, time });
  };
  const dayDrop = (key, band) => (canDrag ? {
    onDragOver: (e) => {
      if (!dragRef.current) return;
      e.preventDefault(); e.dataTransfer.dropEffect = "move";
      setOver((o) => (o && o.key === key && Boolean(o.band) === band && o.min == null ? o : { key, band }));
    },
    onDrop: (e) => {
      if (!dragRef.current) return;
      e.preventDefault();
      const p = posts.find((x) => x.id === dragRef.current.id);
      place(key, band ? "" : (p && p.time) || "");
    },
  } : {});
  const slotDrop = (key) => (canDrag ? {
    onDragOver: (e) => {
      if (!dragRef.current) return;
      e.preventDefault(); e.dataTransfer.dropEffect = "move";
      const min = minutesAt(e, SNAP, dragRef.current.grabPx, true);
      setOver((o) => (o && o.key === key && o.min === min ? o : { key, min }));
    },
    onDrop: (e) => {
      if (!dragRef.current) return;
      e.preventDefault();
      place(key, toHHMM(minutesAt(e, SNAP, dragRef.current.grabPx, true)));
    },
  } : {});

  // ---- Éléments ----
  const who = (p) => (clientNames ? clientNames[p.client_id] || "" : "");
  const label = (p) => [who(p), titleOf(p), statusOf(p).label, p.time].filter(Boolean).join(" · ");
  const fade = (p) => (draggingId === p.id ? "opacity-40" : "");
  const grab = canDrag ? "cursor-grab active:cursor-grabbing" : "";

  const chip = (p) => {
    const tone = toneOf(p), name = who(p);
    return (
      <Clickable key={p.id} onActivate={() => onPostClick(p)} label={label(p)} testid={`cal-post-${p.id}`} drag={dragProps(p)}
        className={`w-full min-w-0 rounded-md border px-1.5 py-[3px] text-[11.5px] leading-snug transition-colors ${tone.chip} ${fade(p)} ${grab}`}>
        <span className="flex items-center gap-1.5 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tone.dot}`} aria-hidden="true" />
          {p.time && <span className="font-bold tabular-nums shrink-0">{p.time}</span>}
          <span className={`truncate ${name ? "font-bold" : ""}`}>{name || titleOf(p)}</span>
        </span>
        {name && <span className="block truncate pl-3">{titleOf(p)}</span>}
      </Clickable>
    );
  };

  const block = ({ p, lane, lanes }, big) => {
    const tone = toneOf(p), st = statusOf(p), name = who(p), w = 100 / lanes;
    return (
      <Clickable key={p.id} onActivate={() => onPostClick(p)} label={label(p)} testid={`cal-post-${p.id}`} drag={dragProps(p)}
        style={{ top: (toMin(p.time) / 60) * HOUR + 1, height: big ? 62 : 46, left: `calc(${lane * w}% + 3px)`, width: `calc(${w}% - 6px)` }}
        className={`absolute z-[1] flex flex-col gap-px overflow-hidden rounded-lg border px-2 py-1 text-[11.5px] leading-tight shadow-sm transition-colors hover:z-[2] ${tone.chip} ${fade(p)} ${grab}`}>
        <span className="flex items-center gap-1.5 min-w-0 whitespace-nowrap">
          <span className="font-bold tabular-nums">{p.time}</span>
          <PlatformIcon post={p} />
          {big ? (
            <>
              <span className="text-marine/80">{(POST_FORMATS[p.format] || {}).label}</span>
              <span className={`inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-semibold ${st.cls}`}>{st.label}</span>
            </>
          ) : name && <span className="font-bold truncate">{name}</span>}
        </span>
        <span className={`font-semibold truncate ${big ? "text-[12.5px]" : ""}`}>{titleOf(p)}</span>
        {big && name && <span className="text-marine/80 truncate">{name}</span>}
      </Clickable>
    );
  };

  const listItem = (p) => {
    const tone = toneOf(p), name = who(p);
    return (
      <Clickable key={p.id} onActivate={() => onPostClick(p)} label={label(p)} testid={`cal-post-${p.id}`}
        className={`grid grid-cols-[46px_minmax(0,1fr)] gap-2 items-start p-2.5 rounded-lg border ${tone.chip}`}>
        <span className={p.time ? "text-xs font-bold tabular-nums pt-px" : "text-[10.5px] font-semibold text-marine/80 leading-tight"}>{p.time || "Sans heure"}</span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold leading-snug">{titleOf(p)}</span>
          <span className="flex items-center gap-1.5 flex-wrap mt-0.5 text-[11.5px] text-marine/80">
            <PlatformIcon post={p} />{name && <span>{name} ·</span>}<span>{statusOf(p).label}</span>
          </span>
        </span>
      </Clickable>
    );
  };

  const dayHead = (c) => {
    const key = iso(c);
    return (
      <div key={key} className="flex flex-col items-center gap-0.5 py-2.5">
        <span className="text-[11px] font-semibold tracking-wider text-marine/80">{DAY_SHORT[(c.getDay() + 6) % 7]}</span>
        <button type="button" onClick={() => openDay(key)} aria-label={`Ouvrir le ${fmtDayLong(c)}`}
          className={`w-10 h-10 rounded-full text-xl font-semibold tabular-nums ${key === today ? "bg-marine text-white" : "hover:bg-marine-50"}`}>
          {c.getDate()}
        </button>
      </div>
    );
  };

  // ---- Vues ----
  const renderTimed = (days, big) => {
    const cols = { gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))` };
    return (
      <div className={days.length > 1 ? "overflow-x-auto" : ""}>
        <div className={days.length > 1 ? "min-w-[760px]" : ""}>
          <div className="grid border-b border-marine/15" style={cols}><div />{days.map(dayHead)}</div>
          <div className="grid border-b border-marine/15" style={cols}>
            <div className="px-2 pt-2 text-[10px] font-semibold leading-tight text-marine/80">Sans heure</div>
            {days.map((c) => {
              const key = iso(c), list = (byDay[key] || []).filter((p) => !p.time);
              return (
                <div key={key} data-testid={`cal-band-${key}`} {...dayDrop(key, true)}
                  onClick={(e) => { if (onCreate && e.target === e.currentTarget) onCreate(key, ""); }}
                  className={`min-w-0 min-h-[34px] p-1 flex flex-col gap-[3px] border-l border-marine/10 ${over && over.key === key && over.band ? "bg-marine-50/70" : ""} ${onCreate ? "cursor-cell" : ""}`}>
                  {list.map(chip)}
                </div>
              );
            })}
          </div>
          <div ref={scrollRef} tabIndex={0} role="region" aria-label="Grille des heures" className="h-[540px] overflow-y-auto">
            <div className="relative grid" style={{ ...cols, height: 24 * HOUR }}>
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                {Array.from({ length: 24 }, (_, h) => (
                  <div key={h} className="absolute left-[56px] right-0 border-t border-marine/10" style={{ top: h * HOUR }}>
                    <span className="absolute -left-[52px] w-11 text-right text-[10.5px] text-marine/80 tabular-nums" style={{ top: h === 0 ? 3 : -8 }}>{pad(h)}:00</span>
                  </div>
                ))}
              </div>
              <div />
              {days.map((c) => {
                const key = iso(c), timed = (byDay[key] || []).filter((p) => p.time);
                const ghost = over && over.key === key && over.min != null ? over.min : null;
                return (
                  <div key={key} data-testid={`cal-slot-${key}`} {...slotDrop(key)}
                    onClick={(e) => { if (onCreate && e.target === e.currentTarget) onCreate(key, toHHMM(minutesAt(e, 30, 0, false))); }}
                    className={`relative border-l border-marine/10 ${onCreate ? "cursor-cell" : ""}`}>
                    {layoutLanes(timed).map((it) => block(it, big))}
                    {ghost != null && (
                      <div className="absolute left-1 right-1 z-[3] rounded-md border-2 border-dashed border-marine/50 bg-marine-50/70 px-1.5 text-[11px] font-bold text-marine pointer-events-none"
                        style={{ top: (ghost / 60) * HOUR, height: big ? 62 : 46 }}>{toHHMM(ghost)}</div>
                    )}
                    {key === today && (
                      <div className="absolute -left-px right-0 z-[4] h-0.5 bg-bordeaux pointer-events-none" style={{ top: (now / 60) * HOUR }} title="Maintenant">
                        <span className="absolute -left-[5px] -top-1 w-2.5 h-2.5 rounded-full bg-bordeaux" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMonth = () => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const start = startOfWeek(new Date(y, m, 1)), end = addDays(startOfWeek(new Date(y, m + 1, 0)), 6);
    const cells = []; for (let c = new Date(start); c <= end; c = addDays(c, 1)) cells.push(c);
    return (
      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-7 border-b border-marine/15">
            {DAY_SHORT.map((d) => <span key={d} className="py-2.5 text-center text-[11px] font-semibold tracking-wider text-marine/80">{d}</span>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((c) => {
              const key = iso(c), list = byDay[key] || [], shown = list.slice(0, 3), more = list.length - shown.length;
              return (
                <div key={key} data-testid={`cal-day-${key}`} {...dayDrop(key, false)}
                  className={`group relative min-w-0 min-h-[118px] p-1.5 flex flex-col gap-1 border-b border-r border-marine/10 [&:nth-child(7n)]:border-r-0 ${over && over.key === key && !over.band && over.min == null ? "bg-marine-50/70" : ""}`}>
                  <div className="relative flex justify-center h-[26px]">
                    <button type="button" onClick={() => openDay(key)} aria-label={`Ouvrir le ${fmtDayLong(c)}`}
                      className={`min-w-[26px] h-[26px] px-1.5 rounded-full text-xs font-semibold tabular-nums ${key === today ? "bg-marine text-white" : c.getMonth() === m ? "hover:bg-marine-50" : "text-muet hover:bg-marine-50"}`}>
                      {c.getDate() === 1 ? fmt(c, { day: "numeric", month: "short" }) : c.getDate()}
                    </button>
                    {onCreate && (
                      <button type="button" onClick={() => onCreate(key, "")} data-testid={`cal-add-${key}`} aria-label={`Nouvelle publication le ${fmtDayLong(c)}`} title="Nouvelle publication"
                        className="absolute right-0 top-0 w-[26px] h-[26px] grid place-items-center rounded-md text-marine/80 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-marine-50 hover:text-marine">
                        <Icons.Plus size={16} />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-[3px] min-w-0">
                    {shown.map(chip)}
                    {more > 0 && (
                      <button type="button" onClick={() => openDay(key)} className="text-left px-1.5 text-[11.5px] font-semibold text-marine/80 hover:text-marine hover:underline">
                        + {more} autre{more > 1 ? "s" : ""}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderYear = () => {
    const y = cursor.getFullYear();
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7 p-4 sm:p-7">
        {Array.from({ length: 12 }, (_, m) => {
          const first = new Date(y, m, 1), start = startOfWeek(first);
          return (
            <div key={m}>
              <button type="button" onClick={() => openMonth(y, m)} className="-ml-1.5 mb-2 px-1.5 py-0.5 rounded-md text-base font-bold hover:bg-marine-50">{cap(fmt(first, { month: "long" }))}</button>
              <div className="grid grid-cols-7 gap-y-1.5 text-center">
                {DAY_INITIAL.map((d, i) => <span key={i} className="pb-0.5 text-[10.5px] font-semibold text-marine/80">{d}</span>)}
                {Array.from({ length: 42 }, (_, i) => {
                  const c = addDays(start, i), key = iso(c);
                  if (c.getMonth() !== m) return <span key={key} className="w-[30px] h-[30px] mx-auto grid place-items-center text-xs text-muet tabular-nums">{c.getDate()}</span>;
                  const list = byDay[key] || [];
                  const top = list.reduce((a, p) => (!a || RANK[statusOf(p).intent] < RANK[statusOf(a).intent] ? p : a), null);
                  return (
                    <button key={key} type="button" onClick={() => openDay(key)}
                      aria-label={`${fmtDayLong(c)}${list.length ? `, ${list.length} publication${list.length > 1 ? "s" : ""}` : ""}`}
                      className={`relative w-[30px] h-[30px] mx-auto grid place-items-center rounded-full text-xs tabular-nums ${key === today ? "bg-marine text-white" : "hover:bg-marine-50"}`}>
                      {c.getDate()}
                      {top && <i className={`absolute -bottom-[5px] left-1/2 -ml-[2.5px] w-[5px] h-[5px] rounded-full ${toneOf(top).dot}`} aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Téléphone : la semaine et le jour deviennent une liste, jour par jour.
  const renderAgenda = (days) => (
    <div className="divide-y divide-marine/10">
      {days.map((c) => {
        const key = iso(c), list = byDay[key] || [];
        return (
          <div key={key} data-testid={`cal-day-${key}`} className="p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <button type="button" onClick={() => openDay(key)} className="text-sm font-bold text-left">
                {cap(fmtDayLong(c))}
                {key === today && <span className="ml-2 px-1.5 py-px rounded-full bg-marine text-white text-[10px] font-semibold align-middle">Aujourd'hui</span>}
              </button>
              {onCreate && (
                <button type="button" onClick={() => onCreate(key, "")} data-testid={`cal-add-${key}`} className="inline-flex items-center gap-1 text-xs text-marine hover:underline">
                  <Icons.Plus size={16} /> Ajouter
                </button>
              )}
            </div>
            {list.length ? <div className="grid gap-2">{list.map(listItem)}</div> : <p className="text-xs text-marine/80">Aucune publication.</p>}
          </div>
        );
      })}
    </div>
  );

  // Téléphone : le mois se resserre en points de couleur, la liste du jour touché s'affiche dessous.
  const renderMonthCompact = () => {
    const y = cursor.getFullYear(), m = cursor.getMonth(), monthKey = `${y}-${pad(m + 1)}`;
    const sel = picked && picked.startsWith(monthKey) ? picked : today.startsWith(monthKey) ? today : `${monthKey}-01`;
    const start = startOfWeek(new Date(y, m, 1)), end = addDays(startOfWeek(new Date(y, m + 1, 0)), 6);
    const cells = []; for (let c = new Date(start); c <= end; c = addDays(c, 1)) cells.push(c);
    return (
      <div className="p-2">
        <div className="grid grid-cols-7">
          {DAY_INITIAL.map((d, i) => <span key={i} className="py-1 text-center text-[10px] font-semibold text-marine/80">{d}</span>)}
          {cells.map((c) => {
            const key = iso(c), list = byDay[key] || [];
            const intents = [...new Set(list.map((p) => statusOf(p).intent))].sort((a, b) => RANK[a] - RANK[b]).slice(0, 3);
            const cls = key === today ? "bg-marine text-white" : key === sel ? "bg-marine-50 ring-1 ring-inset ring-marine" : "";
            return (
              <button key={key} type="button" onClick={() => setPicked(key)} aria-label={fmtDayLong(c)} aria-pressed={key === sel}
                className={`flex flex-col items-center gap-[3px] h-11 pt-1 text-[12.5px] tabular-nums ${c.getMonth() === m ? "" : "text-muet"}`}>
                <span className={`w-[26px] h-[26px] grid place-items-center rounded-full ${cls}`}>{c.getDate()}</span>
                <span className="flex gap-0.5 h-[5px]">{intents.map((it) => <i key={it} className={`w-[5px] h-[5px] rounded-full ${TONE[it].dot}`} aria-hidden="true" />)}</span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-marine/10 mt-2">{renderAgenda([parseISO(sel)])}</div>
      </div>
    );
  };

  let body;
  if (view === "annee") body = renderYear();
  else if (view === "mois") body = narrow ? renderMonthCompact() : renderMonth();
  else if (narrow) body = renderAgenda(shownDays);
  else body = renderTimed(shownDays, view === "jour");

  return (
    <div data-testid="editorial-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => { setCursor(new Date()); setPicked(null); }} data-testid="cal-today"
            className="h-[38px] px-4 rounded-sm border border-marine/25 text-[13.5px] font-semibold text-marine hover:bg-marine-50">Aujourd'hui</button>
          <button type="button" onClick={() => shift(-1)} data-testid="cal-prev" aria-label="Période précédente"
            className="w-[38px] h-[38px] grid place-items-center rounded-sm border border-marine/25 text-marine hover:bg-marine-50"><Icons.ChevronLeft size={16} /></button>
          <button type="button" onClick={() => shift(1)} data-testid="cal-next" aria-label="Période suivante"
            className="w-[38px] h-[38px] grid place-items-center rounded-sm border border-marine/25 text-marine hover:bg-marine-50"><Icons.ChevronRight size={16} /></button>
          <h2 className="ml-1.5 font-display text-xl sm:text-[22px] text-marine tabular-nums" data-testid="cal-period" aria-live="polite">{period}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div role="group" aria-label="Vue du calendrier" className="inline-flex rounded-sm border border-marine/25 overflow-hidden bg-creme">
            {VIEWS.map(([v, l], i) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => { setView(v); setPicked(null); }} data-testid={`cal-view-${v}`}
                className={`h-9 px-3.5 text-[13px] font-semibold ${i ? "border-l border-marine/15" : ""} ${view === v ? "bg-marine text-white" : "text-marine/80 hover:bg-marine-50"}`}>
                {l}
              </button>
            ))}
          </div>
          {toolbarExtra}
        </div>
      </div>
      <div className="bg-sable rounded-sm border border-marine/10 overflow-hidden" onDragEnd={endDrag}>{body}</div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-xs text-marine/80" aria-label="Légende des couleurs">
        {LEGEND.map(([k, l]) => <span key={k} className="inline-flex items-center gap-1.5"><i className={`w-2 h-2 rounded-full ${TONE[k].dot}`} aria-hidden="true" />{l}</span>)}
        {canDrag && <span className="text-marine/80">Glissez une publication pour changer son jour ou son heure.</span>}
      </div>
    </div>
  );
}
